/* ─────────────────────────────────────────────────────────────
   DatePicker — Calendar popover with day/month/year views.
   No external date library needed. Uses Popover primitive.
   ───────────────────────────────────────────────────────────── */

import * as React from 'react'
import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { cn } from '@/lib/cn'

export interface DatePickerProps {
  value?: string // ISO date string (YYYY-MM-DD)
  onValueChange?: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  /** HTML id for the trigger button (enables label association via htmlFor) */
  id?: string
  /** Minimum selectable date (ISO string) */
  min?: string
  /** Maximum selectable date (ISO string) */
  max?: string
}

type CalendarView = 'day' | 'month' | 'year'

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

function parseDate(str: string | undefined): Date | null {
  if (!str) return null
  const d = new Date(str + 'T00:00:00')
  return isNaN(d.getTime()) ? null : d
}

function toIso(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function formatDisplay(str: string | undefined): string {
  const d = parseDate(str)
  if (!d) return ''
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
}

function isToday(d: Date): boolean {
  return isSameDay(d, new Date())
}

interface CalendarDay {
  date: Date
  inMonth: boolean
  disabled: boolean
}

function getCalendarDays(year: number, month: number, minDate: Date | null, maxDate: Date | null): CalendarDay[] {
  const firstOfMonth = new Date(year, month, 1)
  // Monday = 0 based start
  let startDay = firstOfMonth.getDay() - 1
  if (startDay < 0) startDay = 6

  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const days: CalendarDay[] = []

  // Previous month padding
  const prevMonthDays = new Date(year, month, 0).getDate()
  for (let i = startDay - 1; i >= 0; i--) {
    const date = new Date(year, month - 1, prevMonthDays - i)
    days.push({ date, inMonth: false, disabled: isOutOfRange(date, minDate, maxDate) })
  }

  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d)
    days.push({ date, inMonth: true, disabled: isOutOfRange(date, minDate, maxDate) })
  }

  // Next month padding (fill to 42 = 6 rows)
  const remaining = 42 - days.length
  for (let d = 1; d <= remaining; d++) {
    const date = new Date(year, month + 1, d)
    days.push({ date, inMonth: false, disabled: isOutOfRange(date, minDate, maxDate) })
  }

  return days
}

function isOutOfRange(date: Date, min: Date | null, max: Date | null): boolean {
  if (min && date < min) return true
  if (max && date > max) return true
  return false
}

/**
 * Returns true if every day in the given month is outside the min/max range,
 * meaning the user cannot select any day in that month.
 */
function isMonthDisabled(year: number, month: number, min: Date | null, max: Date | null): boolean {
  if (!min && !max) return false
  const firstOfMonth = new Date(year, month, 1)
  const lastOfMonth = new Date(year, month + 1, 0)
  if (max && firstOfMonth > max) return true
  if (min && lastOfMonth < min) return true
  return false
}

/**
 * Returns true if every day in the given year is outside the min/max range.
 */
function isYearDisabled(year: number, min: Date | null, max: Date | null): boolean {
  if (!min && !max) return false
  const jan1 = new Date(year, 0, 1)
  const dec31 = new Date(year, 11, 31)
  if (max && jan1 > max) return true
  if (min && dec31 < min) return true
  return false
}

// ─── Month Grid ───────────────────────────────────────────────

interface MonthGridProps {
  viewYear: number
  viewMonth: number
  selectedDate: Date | null
  minDate: Date | null
  maxDate: Date | null
  focusedMonth: number
  monthCellRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>
  onSelect: (month: number) => void
  onFocusChange: (month: number) => void
  onEscape: () => void
}

function MonthGrid({
  viewYear, viewMonth, selectedDate, minDate, maxDate,
  focusedMonth, monthCellRefs, onSelect, onFocusChange, onEscape,
}: MonthGridProps) {
  const COLS = 3

  function handleKeyDown(e: React.KeyboardEvent, month: number) {
    let next = month
    if (e.key === 'ArrowRight') next = Math.min(11, month + 1)
    else if (e.key === 'ArrowLeft') next = Math.max(0, month - 1)
    else if (e.key === 'ArrowDown') next = Math.min(11, month + COLS)
    else if (e.key === 'ArrowUp') next = Math.max(0, month - COLS)
    else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (!isMonthDisabled(viewYear, month, minDate, maxDate)) onSelect(month)
      return
    }
    else if (e.key === 'Escape') { onEscape(); return }
    else return

    e.preventDefault()
    onFocusChange(next)
    monthCellRefs.current[next]?.focus()
  }

  return (
    <div role="grid" aria-label={`Select month — ${viewYear}`} className="grid grid-cols-3 gap-1">
      {MONTHS_SHORT.map((name, month) => {
        const disabled = isMonthDisabled(viewYear, month, minDate, maxDate)
        const isSelected = selectedDate !== null &&
          selectedDate.getFullYear() === viewYear &&
          selectedDate.getMonth() === month

        return (
          <button
            key={month}
            ref={(el) => { monthCellRefs.current[month] = el }}
            type="button"
            role="gridcell"
            tabIndex={focusedMonth === month ? 0 : -1}
            aria-selected={isSelected}
            aria-disabled={disabled}
            aria-label={name}
            onClick={() => !disabled && onSelect(month)}
            onKeyDown={(e) => handleKeyDown(e, month)}
            className={cn(
              'h-9 flex items-center justify-center text-sm rounded-md transition-colors',
              'focus:outline-none focus:ring-1 focus:ring-ring',
              disabled && 'opacity-25 cursor-not-allowed',
              !disabled && !isSelected && 'hover:bg-surface-muted text-text',
              isSelected && 'bg-primary text-primary-foreground font-semibold',
              !isSelected && month === viewMonth && 'font-semibold text-primary',
            )}
          >
            {name}
          </button>
        )
      })}
    </div>
  )
}

// ─── Year Grid ────────────────────────────────────────────────

interface YearGridProps {
  viewYear: number
  selectedDate: Date | null
  minDate: Date | null
  maxDate: Date | null
  decadePage: number
  focusedYear: number
  yearCellRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>
  onSelect: (year: number) => void
  onFocusChange: (year: number) => void
  onEscape: () => void
  onDecadePrev: () => void
  onDecadeNext: () => void
}

function YearGrid({
  viewYear, selectedDate, minDate, maxDate,
  decadePage, focusedYear, yearCellRefs,
  onSelect, onFocusChange, onEscape, onDecadePrev, onDecadeNext,
}: YearGridProps) {
  const COLS = 4
  const years = Array.from({ length: 12 }, (_, i) => decadePage + i)

  function handleKeyDown(e: React.KeyboardEvent, year: number) {
    const idx = year - decadePage
    let nextIdx = idx
    if (e.key === 'ArrowRight') nextIdx = Math.min(11, idx + 1)
    else if (e.key === 'ArrowLeft') nextIdx = Math.max(0, idx - 1)
    else if (e.key === 'ArrowDown') nextIdx = Math.min(11, idx + COLS)
    else if (e.key === 'ArrowUp') nextIdx = Math.max(0, idx - COLS)
    else if (e.key === 'PageDown') {
      e.preventDefault()
      onDecadeNext()
      requestAnimationFrame(() => { yearCellRefs.current[0]?.focus() })
      return
    }
    else if (e.key === 'PageUp') {
      e.preventDefault()
      onDecadePrev()
      requestAnimationFrame(() => { yearCellRefs.current[0]?.focus() })
      return
    }
    else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (!isYearDisabled(year, minDate, maxDate)) onSelect(year)
      return
    }
    else if (e.key === 'Escape') { onEscape(); return }
    else return

    e.preventDefault()
    const nextYear = decadePage + nextIdx
    onFocusChange(nextYear)
    yearCellRefs.current[nextIdx]?.focus()
  }

  return (
    <div role="grid" aria-label={`Select year, ${decadePage} to ${decadePage + 11}`} className="grid grid-cols-4 gap-1">
      {years.map((year, idx) => {
        const disabled = isYearDisabled(year, minDate, maxDate)
        const isSelected = selectedDate !== null && selectedDate.getFullYear() === year

        return (
          <button
            key={year}
            ref={(el) => { yearCellRefs.current[idx] = el }}
            type="button"
            role="gridcell"
            tabIndex={focusedYear === year ? 0 : -1}
            aria-selected={isSelected}
            aria-disabled={disabled}
            aria-label={String(year)}
            onClick={() => !disabled && onSelect(year)}
            onKeyDown={(e) => handleKeyDown(e, year)}
            className={cn(
              'h-9 flex items-center justify-center text-sm rounded-md transition-colors',
              'focus:outline-none focus:ring-1 focus:ring-ring',
              disabled && 'opacity-25 cursor-not-allowed',
              !disabled && !isSelected && 'hover:bg-surface-muted text-text',
              isSelected && 'bg-primary text-primary-foreground font-semibold',
              !isSelected && year === viewYear && 'font-semibold text-primary',
            )}
          >
            {year}
          </button>
        )
      })}
    </div>
  )
}

// ─── DatePicker ───────────────────────────────────────────────

export function DatePicker({
  value,
  onValueChange,
  placeholder = 'Pick a date',
  disabled = false,
  className,
  id,
  min,
  max,
}: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<CalendarView>('day')

  const selectedDate = parseDate(value)
  const minDate = parseDate(min)
  const maxDate = parseDate(max)

  const [viewYear, setViewYear] = useState(() => selectedDate?.getFullYear() ?? new Date().getFullYear())
  const [viewMonth, setViewMonth] = useState(() => selectedDate?.getMonth() ?? new Date().getMonth())

  const [decadePage, setDecadePage] = useState(
    () => Math.floor((selectedDate?.getFullYear() ?? new Date().getFullYear()) / 12) * 12,
  )

  const [focusedMonth, setFocusedMonth] = useState(() => selectedDate?.getMonth() ?? new Date().getMonth())
  const [focusedYear, setFocusedYear] = useState(() => selectedDate?.getFullYear() ?? new Date().getFullYear())

  const monthCellRefs = useRef<(HTMLButtonElement | null)[]>(Array(12).fill(null))
  const yearCellRefs = useRef<(HTMLButtonElement | null)[]>(Array(12).fill(null))

  const days = useMemo(() => getCalendarDays(viewYear, viewMonth, minDate, maxDate), [viewYear, viewMonth, minDate, maxDate])

  // Keep decadePage in sync when viewYear moves outside the current page
  useEffect(() => {
    const pageStart = Math.floor(viewYear / 12) * 12
    if (viewYear < decadePage || viewYear >= decadePage + 12) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- focus management and view sync — reads and moves DOM focus, which only exists after commit
      setDecadePage(pageStart)
    }
  }, [viewYear]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-focus on entering month view
  useEffect(() => {
    if (view !== 'month') return
    const idx = isMonthDisabled(viewYear, viewMonth, minDate, maxDate)
      ? (Array.from({ length: 12 }, (_, i) => i).find((m) => !isMonthDisabled(viewYear, m, minDate, maxDate)) ?? 0)
      : viewMonth
    // eslint-disable-next-line react-hooks/set-state-in-effect -- focus management and view sync — reads and moves DOM focus, which only exists after commit
    setFocusedMonth(idx)
    monthCellRefs.current[idx]?.focus()
  }, [view]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-focus on entering year view
  useEffect(() => {
    if (view !== 'year') return
    const years = Array.from({ length: 12 }, (_, i) => decadePage + i)
    const target =
      (years.includes(viewYear) && !isYearDisabled(viewYear, minDate, maxDate))
        ? viewYear
        : (years.find((y) => !isYearDisabled(y, minDate, maxDate)) ?? years[0])
    // eslint-disable-next-line react-hooks/set-state-in-effect -- focus management and view sync — reads and moves DOM focus, which only exists after commit
    setFocusedYear(target)
    yearCellRefs.current[target - decadePage]?.focus()
  }, [view]) // eslint-disable-line react-hooks/exhaustive-deps

  const goToPrev = useCallback(() => {
    if (view === 'day') {
      if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1) }
      else { setViewMonth((m) => m - 1) }
    } else if (view === 'month') {
      setViewYear((y) => y - 1)
    } else {
      setDecadePage((p) => p - 12)
    }
  }, [view, viewMonth])

  const goToNext = useCallback(() => {
    if (view === 'day') {
      if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1) }
      else { setViewMonth((m) => m + 1) }
    } else if (view === 'month') {
      setViewYear((y) => y + 1)
    } else {
      setDecadePage((p) => p + 12)
    }
  }, [view, viewMonth])

  const handleSelect = useCallback(
    (day: CalendarDay) => {
      if (day.disabled) return
      onValueChange?.(toIso(day.date))
      setOpen(false)
    },
    [onValueChange],
  )

  const toggleView = useCallback(() => {
    setView((v) => (v === 'day' ? 'month' : v === 'month' ? 'year' : 'day'))
  }, [])

  // Sync view when value changes externally
  useEffect(() => {
    if (selectedDate && open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- focus management and view sync — reads and moves DOM focus, which only exists after commit
      setViewYear(selectedDate.getFullYear())
      setViewMonth(selectedDate.getMonth())
    }
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  const prevLabel = view === 'day' ? 'Previous month' : view === 'month' ? 'Previous year' : 'Previous decade'
  const nextLabel = view === 'day' ? 'Next month' : view === 'month' ? 'Next year' : 'Next decade'
  const toggleLabel =
    view === 'day'
      ? `${MONTHS[viewMonth]} ${viewYear} — click to select month`
      : view === 'month'
      ? `${viewYear} — click to select year`
      : `${decadePage}–${decadePage + 11} — click to return to day view`

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) setView('day')
      }}
    >
      <PopoverPrimitive.Trigger asChild disabled={disabled}>
        <button
          type="button"
          id={id}
          className={cn(
            'flex h-9 w-full items-center gap-2',
            'rounded-lg bg-surface-elevated px-3 py-1 text-sm',
            'border border-input-border transition-colors',
            'focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary',
            'disabled:cursor-not-allowed disabled:opacity-50',
            value ? 'text-text' : 'text-text-subtle',
            className,
          )}
        >
          <Calendar size={14} className="text-text-subtle shrink-0" />
          <span className="flex-1 text-left truncate">
            {value ? formatDisplay(value) : placeholder}
          </span>
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className={cn(
            'z-50 w-[280px] overflow-hidden',
            'rounded-md border border-border bg-surface shadow-lg p-3',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-[0.98] data-[state=open]:zoom-in-[0.98]',
          )}
        >
          {/* Screen-reader view announcement */}
          <div aria-live="polite" aria-atomic="true" className="sr-only">
            {view === 'month' && `Month view, ${viewYear}`}
            {view === 'year' && `Year view, ${decadePage} to ${decadePage + 11}`}
          </div>

          {/* Header: prev / toggle / next */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={goToPrev}
              aria-label={prevLabel}
              className="p-1 hover:bg-surface-muted rounded transition-colors text-text-muted"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              type="button"
              onClick={toggleView}
              aria-label={toggleLabel}
              aria-expanded={view !== 'day'}
              className="text-sm font-semibold text-text hover:text-primary transition-colors"
            >
              {view === 'day' && `${MONTHS[viewMonth]} ${viewYear}`}
              {view === 'month' && String(viewYear)}
              {view === 'year' && `${decadePage}–${decadePage + 11}`}
            </button>
            <button
              type="button"
              onClick={goToNext}
              aria-label={nextLabel}
              className="p-1 hover:bg-surface-muted rounded transition-colors text-text-muted"
            >
              <ChevronRight size={14} />
            </button>
          </div>

          {/* Day view */}
          {view === 'day' && (
            <>
              {/* Day-of-week headers */}
              <div className="grid grid-cols-7 mb-1">
                {DAYS.map((d) => (
                  <div key={d} className="h-8 flex items-center justify-center text-xs font-semibold text-text-subtle">
                    {d}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7">
                {days.map((day, i) => {
                  const selected = selectedDate && isSameDay(day.date, selectedDate)
                  const today = isToday(day.date)

                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleSelect(day)}
                      disabled={day.disabled}
                      className={cn(
                        'h-8 w-full flex items-center justify-center text-xs rounded-md transition-colors',
                        'focus:outline-none focus:ring-1 focus:ring-ring',
                        day.disabled && 'opacity-25 cursor-not-allowed',
                        !day.inMonth && !selected && 'text-text-subtle/40',
                        day.inMonth && !selected && !today && 'text-text hover:bg-surface-muted',
                        today && !selected && 'font-bold text-primary',
                        selected && 'bg-primary text-primary-foreground font-semibold hover:bg-primary-strong',
                      )}
                    >
                      {day.date.getDate()}
                    </button>
                  )
                })}
              </div>

              {/* Today / Clear footer */}
              <div className="mt-2 pt-2 border-t border-border-muted flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    const now = new Date()
                    if (!isOutOfRange(now, minDate, maxDate)) {
                      onValueChange?.(toIso(now))
                      setOpen(false)
                    }
                  }}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Today
                </button>
                {value && (
                  <button
                    type="button"
                    onClick={() => {
                      onValueChange?.('')
                      setOpen(false)
                    }}
                    className="text-xs font-medium text-text-subtle hover:text-text-muted"
                  >
                    Clear
                  </button>
                )}
              </div>
            </>
          )}

          {/* Month view */}
          {view === 'month' && (
            <MonthGrid
              viewYear={viewYear}
              viewMonth={viewMonth}
              selectedDate={selectedDate}
              minDate={minDate}
              maxDate={maxDate}
              focusedMonth={focusedMonth}
              monthCellRefs={monthCellRefs}
              onSelect={(month) => { setViewMonth(month); setView('day') }}
              onFocusChange={setFocusedMonth}
              onEscape={() => setView('day')}
            />
          )}

          {/* Year view */}
          {view === 'year' && (
            <YearGrid
              viewYear={viewYear}
              selectedDate={selectedDate}
              minDate={minDate}
              maxDate={maxDate}
              decadePage={decadePage}
              focusedYear={focusedYear}
              yearCellRefs={yearCellRefs}
              onSelect={(year) => { setViewYear(year); setView('month') }}
              onFocusChange={setFocusedYear}
              onEscape={() => setView('month')}
              onDecadePrev={() => setDecadePage((p) => p - 12)}
              onDecadeNext={() => setDecadePage((p) => p + 12)}
            />
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
